/**
 * Declarative, cross-section FIELD RULES schema (types only).
 *
 * A `FieldRule` describes how one or more fields (in any section) influence a
 * single behaviour of a target field (in any section): visibility, enablement,
 * options, reset, autoFill/autoSet, required, or date constraints.
 *
 * Rules are authored as the global `fieldRules` array in `tpoGenConfig.ts` and
 * flow through the same config pipeline as `formCascadeRules` (validated by
 * `fieldRuleSchema`, adapted in `feConfigAdapter`, consumed via
 * `useTpoGenConfig().fieldRules`). They MUST stay JSON-serializable: compute
 * effects and option sources reference behaviour by string name; the runtime
 * engine resolves those names to functions/resolvers.
 *
 * Design reference: `config/docs/fieldRulesDesign.md`.
 */

/** Form section a field belongs to. */
export type Scope = 'event' | 'promo' | 'plan';

/**
 * Scopes the engine can DERIVE UI state for. Adds the target-only `metric` scope
 * (the editable promo/spend grid columns) and the target-only `control` scope
 * (UI affordances that aren't form fields - e.g. the "Add tactic" button/tab) on
 * top of the form scopes. Neither `metric` nor `control` is a form: they have no
 * store value or setter. `metric` carries `applicability` rules; `control` carries
 * `enablement` / `visibility` rules targeting `control:<name>`, both derived
 * against form values + ctx.
 */
export type DerivableScope = Scope | 'metric' | 'control';

/**
 * Globally-unique, flat field address. `<scope>:<fieldName>` for form fields,
 * `metric:<metricKey>` for a grid metric (target-only), `control:<name>` for a UI
 * control such as an action button (target-only), or `ctx:<key>` for non-field
 * context (mode, isEdit, hasEntityId, planYear). The `:` delimiter is deliberate -
 * `.` already means an apiKey payload path.
 */
export type QualifiedId = `${Scope}:${string}` | `metric:${string}` | `control:${string}` | `ctx:${string}`;

export type Operator =
    | 'eq'
    | 'neq'
    | 'in'
    | 'notIn'
    | 'contains'
    | 'isEmpty'
    | 'isNotEmpty'
    | 'truthy'
    | 'falsy'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte';

/** A single predicate over one field/context value. */
export interface Condition {
    source: QualifiedId;
    op: Operator;
    value?: unknown;
}

/** Boolean composition of conditions. */
export interface ConditionGroup {
    all?: RuleCondition[];
    any?: RuleCondition[];
    not?: RuleCondition;
}

export type RuleCondition = Condition | ConditionGroup;

export interface RuleOption {
    label: string;
    value: string;
    disabled?: boolean;
}

/** Where an autoFill / autoSet value comes from. */
export type ValueSource =
    | { from: 'field'; field: QualifiedId } // copy another field's value (cross-section ok)
    | { from: 'static'; value: unknown } // autoSet a constant
    | { from: 'option'; field: QualifiedId; pick: 'label' | 'value' } // derive from a selected option
    | { from: 'compute'; fn: string }; // named FE fn resolved at runtime (e.g. 'generatePromoName')

/** How a field's option list is produced. */
export type OptionsSource =
    | { kind: 'static'; options: RuleOption[] }
    | { kind: 'mapByField'; field: QualifiedId; map: Record<string, RuleOption[]> }
    | { kind: 'dynamic'; key: string; byField?: QualifiedId } // resolved at runtime (e.g. promoType / tactic)
    | { kind: 'filterByParent'; parentField: QualifiedId; dimension: string; attributeType: string } // dimSelect
    | { kind: 'disableByMap'; field: QualifiedId; map: Record<string, string[]>; defaultEnabled?: string[] };

export interface DateConstraint {
    minField?: QualifiedId;
    maxField?: QualifiedId;
    disablePast?: boolean;
    minFromContext?: string; // e.g. 'planYearStart'
    allowedYears?: 'currentAndNext';
}

export type ResetTo = 'undefined' | 'emptyArray' | 'null';

export type Effect =
    | { kind: 'visibility'; show: boolean }
    | { kind: 'enablement'; enable: boolean }
    | { kind: 'required'; required: boolean }
    | { kind: 'reset'; to?: ResetTo }
    | { kind: 'autoFill'; value: ValueSource; overwrite?: boolean }
    | { kind: 'options'; source: OptionsSource }
    | { kind: 'dateConstraints'; constraints: DateConstraint }
    // Metric grid applicability (target-only `metric:<key>` scope). Kept distinct
    // from `visibility` so metric rules read clearly apart from form-cascade rules.
    | { kind: 'applicability'; applicable: boolean };

export type EffectKind = Effect['kind'];

/** Reactive effects fire on a trigger transition; the rest are derived on read. */
export const REACTIVE_EFFECT_KINDS: ReadonlySet<EffectKind> = new Set<EffectKind>(['reset', 'autoFill']);

export interface FieldRule {
    /** Stable, globally-unique id (debug + conflict tracing). */
    id: string;
    /** Field(s) the effect applies to (any section). */
    target: QualifiedId | QualifiedId[];
    effect: Effect;
    /** Condition over any field(s)/context; omitted => always applies. */
    when?: RuleCondition;
    /** For reactive effects; defaults to the sources used in `when`. */
    triggers?: QualifiedId[];
}

export type RuleSet = FieldRule[];
